import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "./baseQuery";
import { todayString } from "../app/groups/components/memberEntry/memberDayHelpers";
import { bulkAssignBody } from "../app/groups/bulkGoals/goalItems";

/** Toplu atama 500 üyeye kadar yazar; mobildeki gibi 60 sn beklenir. */
const BULK_ASSIGN_TIMEOUT_MS = 60000;

export const managementApi = createApi({
  reducerPath: "panelManagement",
  baseQuery: axiosBaseQuery(),
  // Sekmeye geri dönüşte ve bağlantı gelince yenile; bir bileşen mount olduğunda
  // cache 30 sn'den eskiyse tekrar çek. Böylece sayfa yenilemeye gerek kalmaz.
  refetchOnFocus: true,
  refetchOnReconnect: true,
  refetchOnMountOrArgChange: 30,
  tagTypes: ["ManagedGroups", "ManagedGroup", "GroupStats", "GroupSettings"],
  endpoints: (builder) => ({
    getManagedGroups: builder.query({
      query: () => ({
        url: "/api/panel/groups/get-managed",
        method: "POST",
        data: {},
      }),
      providesTags: ["ManagedGroups"],
    }),
    getManagedGroup: builder.query({
      query: ({ groupId }) => ({
        url: "/api/panel/groups/get-managed-detail",
        method: "POST",
        data: { groupId },
      }),
      providesTags: (result, error, { groupId }) => [
        { type: "ManagedGroup", id: groupId },
      ],
    }),
    createGroup: builder.mutation({
      query: ({ groupName, description }) => ({
        url: "/api/panel/groups/create",
        method: "POST",
        data: { groupName, description: description || "" },
      }),
      invalidatesTags: ["ManagedGroups"],
    }),
    updateGroupName: builder.mutation({
      query: ({ groupId, groupName }) => ({
        url: "/api/panel/groups/update-name",
        method: "POST",
        data: { groupId, groupName },
      }),
      invalidatesTags: (result, error, { groupId }) => [
        { type: "ManagedGroup", id: groupId },
        "ManagedGroups",
      ],
    }),
    // Grup ayarları (yalnız yöneticiler): bildirim anahtarları, geçmiş gün
    // düzenleme penceresi ve üyelerin birbirinin hedeflerini görmesi. Güncelleme kısmi gider; yanıtta tüm ayarlar döner.
    getGroupSettings: builder.query({
      query: ({ groupId }) => ({
        url: "/api/panel/groups/get-settings",
        method: "POST",
        data: { groupId },
      }),
      providesTags: (result, error, { groupId }) => [{ type: "GroupSettings", id: groupId }],
    }),
    updateGroupSettings: builder.mutation({
      query: ({ groupId, editWindowDays, notifications, membersSeeGoals }) => {
        const data = { groupId };
        if (editWindowDays !== undefined) data.editWindowDays = editWindowDays;
        if (notifications !== undefined) data.notifications = notifications;
        if (membersSeeGoals !== undefined) data.membersSeeGoals = membersSeeGoals;
        return { url: "/api/panel/groups/update-settings", method: "POST", data };
      },
      invalidatesTags: (result, error, { groupId }) => [{ type: "GroupSettings", id: groupId }],
    }),
    deleteGroup: builder.mutation({
      query: ({ groupId }) => ({
        url: "/api/panel/groups/delete",
        method: "POST",
        data: { groupId },
      }),
      invalidatesTags: ["ManagedGroups"],
    }),
    addGroupMember: builder.mutation({
      query: ({ groupId, email, code }) => ({
        url: "/api/panel/groups/add-member",
        method: "POST",
        data: { groupId, email, code },
      }),
      // Üye listesi değişince ilerleme istatistikleri de değişiyor.
      invalidatesTags: (result, error, { groupId }) => [
        { type: "ManagedGroup", id: groupId },
        "ManagedGroups",
        "GroupStats",
      ],
    }),
    removeGroupMember: builder.mutation({
      query: ({ groupId, email }) => ({
        url: "/api/panel/groups/remove-member",
        method: "POST",
        data: { groupId, email },
      }),
      invalidatesTags: (result, error, { groupId }) => [
        { type: "ManagedGroup", id: groupId },
        "ManagedGroups",
        "GroupStats",
      ],
    }),
    addGroupManager: builder.mutation({
      // permissions: { canAddMembers, canRemoveMembers }; sunucu ekleyenin
      // kendi yetkisiyle sınırlar. Verilmezse ikisi de açık.
      query: ({ groupId, email, code, permissions }) => ({
        url: "/api/panel/groups/add-manager",
        method: "POST",
        data: { groupId, email, code, ...(permissions ? { permissions } : {}) },
      }),
      invalidatesTags: (result, error, { groupId }) => [
        { type: "ManagedGroup", id: groupId },
        "ManagedGroups",
      ],
    }),
    removeGroupManager: builder.mutation({
      query: ({ groupId, email }) => ({
        url: "/api/panel/groups/remove-manager",
        method: "POST",
        data: { groupId, email },
      }),
      invalidatesTags: (result, error, { groupId }) => [
        { type: "ManagedGroup", id: groupId },
        "ManagedGroups",
      ],
    }),
    addQrTemplate: builder.mutation({
      query: ({ groupId, goalTemplates, groupName }) => ({
        url: "/api/panel/groups/add-qr-template",
        method: "POST",
        data: { groupId, goalTemplates, ...(groupName ? { groupName } : {}) },
      }),
      invalidatesTags: (result, error, { groupId }) => [
        { type: "ManagedGroup", id: groupId },
      ],
    }),
    // QR şablonunu düzenle: kalemler takip bilgileriyle baştan yazılır.
    updateQrTemplate: builder.mutation({
      query: ({ groupId, templateId, goalTemplates, groupName }) => ({
        url: "/api/panel/groups/update-qr-template",
        method: "POST",
        data: { groupId, templateId, goalTemplates, ...(groupName ? { groupName } : {}) },
      }),
      invalidatesTags: (result, error, { groupId }) => [
        { type: "ManagedGroup", id: groupId },
      ],
    }),
    deleteQrTemplate: builder.mutation({
      query: ({ groupId, templateId }) => ({
        url: "/api/panel/groups/delete-qr-template",
        method: "POST",
        data: { groupId, templateId },
      }),
      invalidatesTags: (result, error, { groupId }) => [
        { type: "ManagedGroup", id: groupId },
      ],
    }),
    getGroupStats: builder.query({
      query: ({ groupId, startDate, endDate }) => ({
        url: "/api/panel/groups/get-stats",
        method: "POST",
        data: { groupId, startDate, endDate, groupIds: groupId },
      }),
      providesTags: (result, error, { groupId, startDate, endDate }) => [
        { type: "GroupStats", id: `${groupId}/${startDate}/${endDate}` },
      ],
    }),
    addUserGoal: builder.mutation({
      // Takip alanları (unit, activity, workId, totalValue, planDays,
      // weekDays) goalRules.toGoalRequest'ten olduğu gibi gider.
      query: ({ groupId, targetUserId, ...goal }) => ({
        url: "/api/panel/groups/add-user-goal",
        method: "POST",
        data: { targetUserId, ...goal, localDate: todayString() },
      }),
      invalidatesTags: (result, error, { groupId }) => [
        { type: "ManagedGroup", id: groupId },
        "GroupStats",
      ],
    }),
    updateUserGoal: builder.mutation({
      query: ({ groupId, targetUserId, ...goal }) => ({
        url: "/api/panel/groups/update-user-goal",
        method: "POST",
        data: { targetUserId, ...goal, localDate: todayString() },
      }),
      invalidatesTags: (result, error, { groupId }) => [
        { type: "ManagedGroup", id: groupId },
        "GroupStats",
      ],
    }),
    deleteUserGoal: builder.mutation({
      // Aynı adda okuma ve ezber hedefi ayrı: biçim de gider.
      query: ({ groupId, targetUserId, goalLabel, activity }) => ({
        url: "/api/panel/groups/delete-user-goal",
        method: "POST",
        data: { targetUserId, goalLabel, ...(activity ? { activity } : {}), localDate: todayString() },
      }),
      invalidatesTags: (result, error, { groupId }) => [
        { type: "ManagedGroup", id: groupId },
        "GroupStats",
      ],
    }),
    // Kişi önizleme: e-posta + kişisel kod doğruysa ad, gruptaki durumu,
    // hedefleri, grupları ve istatistik özeti. Mobil uç; yalnız JWT ister.
    lookupPerson: builder.mutation({
      query: ({ groupId, email, code, purpose }) => ({
        url: "/api/groups/lookup-person",
        method: "POST",
        data: { groupId, email, code, purpose },
      }),
    }),
    // Toplu hedef atama; dryRun ile yalnız önizleme (hiçbir şey yazılmaz).
    bulkAssignGoals: builder.mutation({
      query: ({ groupId, memberIds, goals, templateId, dryRun }) => ({
        url: "/api/groups/bulk-assign-goals",
        method: "POST",
        data: bulkAssignBody({ groupId, memberIds, goals, templateId, dryRun, localDate: todayString() }),
        timeout: BULK_ASSIGN_TIMEOUT_MS,
      }),
      invalidatesTags: (result, error, { groupId, dryRun }) =>
        dryRun ? [] : [{ type: "ManagedGroup", id: groupId }, "GroupStats"],
    }),
  }),
});

export const {
  useGetManagedGroupsQuery,
  useGetManagedGroupQuery,
  useCreateGroupMutation,
  useUpdateGroupNameMutation,
  useGetGroupSettingsQuery,
  useUpdateGroupSettingsMutation,
  useDeleteGroupMutation,
  useAddGroupMemberMutation,
  useRemoveGroupMemberMutation,
  useAddGroupManagerMutation,
  useRemoveGroupManagerMutation,
  useAddQrTemplateMutation,
  useUpdateQrTemplateMutation,
  useDeleteQrTemplateMutation,
  useGetGroupStatsQuery,
  useAddUserGoalMutation,
  useUpdateUserGoalMutation,
  useDeleteUserGoalMutation,
  useLookupPersonMutation,
  useBulkAssignGoalsMutation,
} = managementApi;
